// src/pages/HomePage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Stack,
  Card, 
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  AutoGraph as AutoGraphIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import Navbar from '../components/core/Navbar';
import Footer from '../components/core/Footer';

const HomePage = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/models');
  };

  return (
    <>
      <Navbar />
      <Box>
        {/* Hero Section */}
        <Box 
          sx={{ 
            bgcolor: 'background.paper',
            pt: 8,
            pb: 6,
            textAlign: 'center'
          }}
        >
          <Container maxWidth="md">
            <Typography
              component="h1"
              variant="h2"
              color="text.primary"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              Join the Numerai Tournament and grow your stake with us
            </Typography>
            <Typography variant="h5" color="text.secondary" paragraph>
              Let us put your money to work in a system used by AI-powered hedge funds. 
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleGetStarted}
              sx={{ 
                mt: 4,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                backgroundColor: '#00C853', // Signal Green
                '&:hover': {
                  backgroundColor: '#00A644' // Slightly darker green for hover state
                }
              }}
            >
              Start Earning
            </Button>
          </Container>
        </Box>

        {/* The Why Section */}
        <Box sx={{ py: 8, bgcolor: 'background.default' }}>
          <Container maxWidth="md">
            <Typography variant="h3" component="h2" gutterBottom textAlign="center">
              You don't need to become a quant to benefit from intelligent finance
            </Typography>
            <List sx={{ mt: 4 }}>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#00C853' }} /> {/* Signal Green */}
                </ListItemIcon>
                <ListItemText 
                  primary="You've got a little NMR, or you're curious"
                  secondary="Start with any amount - no minimum required"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#00C853' }} /> {/* Signal Green */}
                </ListItemIcon>
                <ListItemText 
                  primary="You want it to grow — but you're not looking for another job"
                  secondary="Set it and forget it - we handle the rest"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#00C853' }} /> {/* Signal Green */}
                </ListItemIcon>
                <ListItemText 
                  primary="You want passive income that feels legit, not risky"
                  secondary="Backed by real AI and proven performance"
                />
              </ListItem>
            </List>
          </Container>
        </Box>

        {/* How It Works */}
        <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
          <Container maxWidth="md">
            <Typography variant="h3" component="h2" gutterBottom textAlign="center">
              How It Works
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mt: 2 }}>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    1. Pick a model
                  </Typography>
                  <Typography>
                    Choose from our curated list of top-performing models
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    2. Stake NMR
                  </Typography>
                  <Typography>
                    We'll walk you through the simple staking process
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    3. Earn automatically
                  </Typography>
                  <Typography>
                    Earn 5 days a week — no code, no spreadsheets, no stress
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </Container>
        </Box>

        {/* Why It's Smart */}
        <Box sx={{ py: 8, bgcolor: 'background.default' }}>
          <Container maxWidth="md">
            <Typography variant="h3" component="h2" gutterBottom textAlign="center">
              Why It's Smart
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mt: 2 }}>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <AutoGraphIcon sx={{ fontSize: 40, color: '#00C853', mb: 2 }} /> {/* Signal Green */}
                  <Typography variant="h6" gutterBottom>
                    AI-Powered Predictions
                  </Typography>
                  <Typography>
                    Connected to the Numerai Tournament — a crowdsourced hedge fund powered by data science
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <SecurityIcon sx={{ fontSize: 40, color: '#00C853', mb: 2 }} /> {/* Signal Green */}
                  <Typography variant="h6" gutterBottom>
                    Fully Non-Custodial
                  </Typography>
                  <Typography>
                    You keep control of your funds at all times
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardContent>
                  <SpeedIcon sx={{ fontSize: 40, color: '#00C853', mb: 2 }} /> {/* Signal Green */}
                  <Typography variant="h6" gutterBottom>
                    Proven Performance
                  </Typography>
                  <Typography>
                    5+ years of real-world performance history
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </Container>
        </Box>

        {/* Final CTA */}
        <Box sx={{ py: 8, bgcolor: 'background.paper' }}>
          <Container maxWidth="md" sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" gutterBottom>
              Smarter crypto starts here
            </Typography>
            <Typography variant="h5" color="text.secondary" paragraph>
              No code. No trading. Just results.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleGetStarted}
              sx={{ 
                mt: 4,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                backgroundColor: '#00C853', // Signal Green
                '&:hover': {
                  backgroundColor: '#D4AF37' // Gold on hover
                }
              }}
            >
              Get Started
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Only takes 2 minutes to set up
            </Typography>
          </Container>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default HomePage;
